import snakeCaseKeys from 'snakecase-keys';
import { Address, InsuranceCompany, User } from '../../resources';
import { restClient } from '../../client';
import { CamelCaseKeys, YesNo } from '@uplift/types';

export enum ValidationStatus {
  active = 'active',
  activeMedicaidInn = 'active_medicaid_inn',
  activeOutOfNetwork = 'active_out_of_network',
  activeMedicaidOon = 'active_medicaid_oon',
  inactive = 'inactive',
  needsReview = 'needs_review',
  manuallyVerified = 'manually_verified',
  pending = 'pending'
}

export type PatientResponsibility = {
  inNetworkPsychIntake?: number;
  inNetworkTherapyIntake?: number;
  inNetworkPsychFollowUp?: number;
  inNetworkTherapyFollowUp?: number;
  outOfNetworkPsychIntake?: number;
  outOfNetworkTherapyIntake?: number;
  outOfNetworkPsychFollowUp?: number;
  outOfNetworkTherapyFollowUp?: number;
  manualPatientResponsibility?: number;
};

export type InsuranceDetails = {
  insuranceId: string;
  status?: ValidationStatus | null;
  verifiedBy: string | null;
  copay: number | null;
  coinsurance: number | null;
  remainingDeductible: number | null;
  patientResponsibility: PatientResponsibility | undefined;
  updatedAt?: string;
  payorName?: string;
  insuranceTypeCode?: string;
  isInNetwork?: boolean;
};

export type GetInsurancesResponse = {
  data: {
    insurances: Insurance[];
    insuranceCompanies: InsuranceCompany[];
    userData: User[];
    validationDetails: InsuranceDetails[];
  };
};

export type GetArchivedInsurancesResponse = {
  data: {
    archivedInsurances: Insurance[];
    insuranceCompanies: InsuranceCompany[];
    userData: User[];
    details: InsuranceDetails[];
  };
};

export type InsuranceDetailsResponse = {
  data: {
    insuranceId: string;
    status: string;
    verifiedBy: string | null;
    copay: number | null;
    coinsurance: number | null;
    remainingDeductible: number | null;
  };
};

export type CreateInsuranceInput = {
  companyId: string;
  subscriberId: string;
  groupNumber: string;
  isPrimary: boolean;
  isMedicaid?: YesNo;
  cardFrontAssetId: string;
  cardBackAssetId: string;
  isSecondaryInsurance: boolean;
  patientId?: string;
  firstName: string;
  lastName: string;
  dob: string;

  address: Address;
  archiveReason?: InsuranceArchiveReason;
  shouldSwapInsurances?: boolean;

  copayAmountCents?: number;
};

export enum InsuranceArchiveReason {
  insuranceIsInactive = 'insurance_is_inactive',
  planWasOon = 'plan_was_oon_and_client_agreed_to_self_pay',
  insuranceCardNotMatched = 'insurance_card_photo_does_not_match_submitted_details',
  insuranceDetailsWereChanged = 'insurance_details_were_changed',
  idNotFound = 'id_not_found',
  incorrectDob = 'incorrect_dob',
  oonMedicaid = 'oon_medicaid',
  coordinationOfBenefitsIssue = 'coordination_of_benefits_issue',
  insuranceOonAndSessionsCanceled = 'insurance_oon_and_sessions_canceled',
  priorAuthNotObtained = 'prior_auth_not_obtained',
  insuranceDoesNotCoverServices = 'insurance_benefits_do_not_cover_mental_health_services'
}


export type Insurance = CreateInsuranceInput & {
  id: string;
  userId: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  archivedAt?: string;
  archivedBy?: string;
  archiveReason?: InsuranceArchiveReason;
};

export type DeleteInsuranceInput = {
  replaceWithSecondary?: boolean;
  patientId?: string;
  archiveReason?: string;
};

export const getArchiveReasonLabel = (reason: InsuranceArchiveReason) => {
  switch (reason) {
    case InsuranceArchiveReason.planWasOon:
      return 'Plan was OON and client agreed to self pay';
    case InsuranceArchiveReason.insuranceCardNotMatched:
      return 'Insurance card photo doesn’t match submitted details';
    case InsuranceArchiveReason.insuranceIsInactive:
      return 'Insurance is inactive';
    case InsuranceArchiveReason.idNotFound:
      return 'Insurance is inactive: member ID not found';
    case InsuranceArchiveReason.incorrectDob:
      return 'Insurance is inactive: incorrect DOB';
    case InsuranceArchiveReason.insuranceDetailsWereChanged:
      return 'Insurance details were changed';
    case InsuranceArchiveReason.oonMedicaid:
      return 'Benefits returned an Out of Network Medicaid plan';
    case InsuranceArchiveReason.coordinationOfBenefitsIssue:
      return 'Coordination of benefits issue';
    case InsuranceArchiveReason.insuranceOonAndSessionsCanceled:
      return 'Insurance out of network and sessions canceled';
    case InsuranceArchiveReason.priorAuthNotObtained:
      return 'Prior auth not obtained';
    case InsuranceArchiveReason.insuranceDoesNotCoverServices:
      return 'Insurance benefits do not cover mental health services';
    default:
      return 'Unknown reason';
  }
};

export const getInsurances = async (userId: string, includeArchived = false) => {
  return restClient.request<GetInsurancesResponse>(`/api/v1/user/${userId}/insurances`, {
    method: 'GET',
    params: {
      include_archived: includeArchived,
    }
  });
};

export const getInsurancesDetails = async (insurance_ids: string[]) => {
  return restClient.request<InsuranceDetailsResponse>('/api/v1/insurance/details', {
    method: 'POST',
    data: {
      insurance_ids: insurance_ids
    }
  });
};

export const getArchivedInsurances = async (userId: string) => {
  return restClient.request<GetArchivedInsurancesResponse>(`/api/v1/user/${userId}/archived_insurances`, {
    method: 'GET',
  });
};

export const updateInsurance = async (insuranceId: string, data: CreateInsuranceInput) => {
  // NOTE:Insurance records are immutable, we create a new one and
  // previous insurance if any will be automatically archived.
  // If we ever support multiple insurances in future,
  // then we will simply change this part to edit that one (which will archive current record).
  return createInsurance(data);
};

export const createInsurance = async (data: CreateInsuranceInput) => {
  return restClient.request<{data: Insurance}>('/api/v1/insurance', { method: 'POST', data: snakeCaseKeys(data) });
};

export const deleteInsurance = async (insuranceId: string, data?: DeleteInsuranceInput) => {
  if (!insuranceId) return;
  return restClient.request<null>(`/api/v1/insurance/delete_or_replace/${insuranceId}`, { method: 'POST', data: snakeCaseKeys(data ?? {}) });
};

export type PatientPaymentResponsibility = CamelCaseKeys<{
  id: string;
  user_id: string;
  copay_amount_cents: number;
  is_deductible: boolean;
  deductible_amount_cents: number;
  archived_at?: string;
  created_at: string;
}>;

export const getPatientPaymentResponsibility = async (userId: string) => {
  return restClient.request<{
    data: {
      current: PatientPaymentResponsibility;
      archived: PatientPaymentResponsibility[];
    };
  }>(`/api/v1/admin/patient/${userId}/payment_responsibility`);
};


export const setPatientPaymentResponsibility = async (
  userId: string,
  data:
  | { copayAmountCents: number; isDeductible: false }
  | { copayAmountCents: number; isDeductible: true; deductibleAmountCents: number }
) => {

  return restClient.request<null>(`/api/v1/admin/patient/${userId}/payment_responsibility`, {
    method: 'POST',
    data: snakeCaseKeys(data)
  });
};


export type InsuranceManualVerificationInput = {
  insuranceId: string;
  isInNetwork: boolean;

  patientResponsibilityCents: number;
  remainingDeductibleCents: number;
  copayCents: number | undefined;
  coinsurancePercentage: number | undefined;
};

export const insuranceManualVerification = (input: InsuranceManualVerificationInput) => {
  return restClient.request('/api/v1/insurance/set_validation_status_manually_verified', {
    method: 'POST',
    data: snakeCaseKeys(input)
  });
};